// Feature Requests page — Internal Admin Portal
// DriverWatch staff can view, approve, and close feature requests from all MSP orgs

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config";

const API_URL = config.API_URL;

export default function FeatureRequests() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("internal_token");

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (!token) { navigate("/login"); return; }
    fetchRequests();
  }, []);

  async function fetchRequests() {
    try {
      const res = await axios.get(`${API_URL}/internal/feature-requests`, authHeaders);
      setRequests(res.data);
    } catch (err) {
      console.error("Failed to load feature requests", err);
    } finally {
      setLoading(false);
    }
  }

  async function updateStatus(id, status) {
    try {
      await axios.patch(`${API_URL}/internal/feature-requests/${id}?status=${status}`, {}, authHeaders);
      setRequests(prev => prev.map(r => r.id === id ? { ...r, status } : r));
    } catch (err) {
      alert("Failed to update status. Please try again.");
    }
  }

  function statusBadge(status) {
    if (status === "approved") return "bg-green-900 text-green-300";
    if (status === "closed") return "bg-gray-700 text-gray-400";
    return "bg-yellow-900 text-yellow-300";
  }

  function statusLabel(status) {
    if (status === "approved") return "✅ Approved";
    if (status === "closed") return "🔒 Closed";
    return "⏳ Pending";
  }

  const filtered = filter === "all" ? requests : requests.filter(r => r.status === filter);

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === "pending").length,
    approved: requests.filter(r => r.status === "approved").length,
    closed: requests.filter(r => r.status === "closed").length,
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">

      {/* Navbar */}
      <nav className="bg-gray-900 border-b border-gray-800 px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
          <img src="/logo-symbol.svg" alt="AleroSys" className="h-8 w-8" />
          <div className="flex items-center gap-2">
            <span className="text-white font-bold text-lg">AleroSys</span>
            <span className="text-gray-500 text-lg font-light">—</span>
            <span className="text-red-400 text-lg font-semibold">DriverWatch Admin</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="text-gray-400 hover:text-white text-sm transition-colors">← Dashboard</button>
          <button onClick={() => navigate("/users")} className="text-gray-400 hover:text-white text-sm border border-gray-700 hover:border-gray-500 px-3 py-1 rounded-lg transition-colors">Staff</button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-10">
        <h1 className="text-2xl font-bold text-white mb-1">Feature Requests</h1>
        <p className="text-gray-400 text-sm mb-8">Review and manage feature requests submitted by MSP organisations.</p>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6">
          {["all", "pending", "approved", "closed"].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors capitalize ${
                filter === f
                  ? "bg-blue-600 text-white"
                  : "bg-gray-800 text-gray-400 hover:text-white"
              }`}
            >
              {f} <span className="ml-1 opacity-70">({counts[f]})</span>
            </button>
          ))}
        </div>

        {/* Requests list */}
        {loading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="bg-gray-900 border border-gray-800 rounded-xl p-8 text-center text-gray-500 text-sm">
            No requests found.
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(req => (
              <div key={req.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-white font-medium text-sm">{req.title}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusBadge(req.status)}`}>
                        {statusLabel(req.status)}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm whitespace-pre-wrap mb-2">{req.description}</p>
                    <p className="text-gray-600 text-xs">
                      {req.org_name} · {req.submitted_by} ({req.submitted_by_email}) · {new Date(req.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                    </p>
                  </div>

                  {/* Action buttons */}
                  <div className="flex flex-col gap-2 min-w-fit">
                    {req.status !== "approved" && (
                      <button
                        onClick={() => updateStatus(req.id, "approved")}
                        className="bg-green-700 hover:bg-green-600 text-white text-xs px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Approve
                      </button>
                    )}
                    {req.status !== "closed" && (
                      <button
                        onClick={() => updateStatus(req.id, "closed")}
                        className="bg-gray-700 hover:bg-gray-600 text-white text-xs px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Close
                      </button>
                    )}
                    {req.status !== "pending" && (
                      <button
                        onClick={() => updateStatus(req.id, "pending")}
                        className="bg-yellow-800 hover:bg-yellow-700 text-white text-xs px-3 py-1.5 rounded-lg transition-colors"
                      >
                        Reopen
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}